/**
 * POS Health Monitor Actor
 *
 * Background polling actor that monitors Stripe Terminal Reader health.
 * Designed to be spawned early in the flow and run continuously.
 *
 * Emits events when reader status changes, enabling:
 * - Pre-flight health checks before payment
 * - Early warning when reader goes offline
 * - Automatic fallback to manual card entry
 */

import { datadogLogs } from "@datadog/browser-logs";
import type Stripe from "stripe";
import { fromCallback } from "xstate";
import { z } from "zod";

/**
 * POS Health Status
 */
export type POSHealthStatus = "unknown" | "healthy" | "degraded" | "offline";

/**
 * Input Schema
 */
export const POSHealthMonitorInputSchema = z.object({
  readerId: z.string().optional(),
  pollIntervalMs: z.number().optional().default(30000), // 30s default
  degradedThresholdMs: z.number().optional().default(60000), // 60s before degraded
});

export type POSHealthMonitorInput = z.infer<typeof POSHealthMonitorInputSchema>;

/**
 * Events emitted by the health monitor
 */
export type POSHealthEvent =
  | { type: "POS_HEALTH.HEALTHY"; readerId: string; lastSeen: Date }
  | { type: "POS_HEALTH.DEGRADED"; readerId: string; reason: string }
  | { type: "POS_HEALTH.OFFLINE"; readerId: string; reason: string }
  | { type: "POS_HEALTH.ERROR"; error: string }
  | { type: "POS_HEALTH.NO_READER" };

/**
 * Health check result
 */
interface HealthCheckResult {
  status: POSHealthStatus;
  readerId?: string;
  lastSeen?: Date;
  reason?: string;
  reader?: Stripe.Terminal.Reader;
}

/**
 * Check reader health
 */
async function checkReaderHealth(
  readerId: string,
  degradedThresholdMs: number,
): Promise<HealthCheckResult> {
  try {
    const response = await fetch(
      `/api/stripe/get-terminal-reader?readerId=${encodeURIComponent(readerId)}`,
    );

    if (!response.ok) {
      return {
        status: "offline",
        readerId,
        reason: "Reader not found in Stripe",
      };
    }

    const reader = (await response.json()) as Stripe.Terminal.Reader;

    // Check reader status
    if (reader.status === "offline") {
      return {
        status: "offline",
        readerId,
        reason: "Reader reported offline by Stripe",
        reader,
      };
    }

    // Check last_seen_at for staleness (degraded state)
    // Note: last_seen_at is available on S700 readers
    const lastSeenAt = reader.last_seen_at
      ? new Date(reader.last_seen_at * 1000)
      : null;

    if (lastSeenAt) {
      const staleDuration = Date.now() - lastSeenAt.getTime();
      if (staleDuration > degradedThresholdMs) {
        return {
          status: "degraded",
          readerId,
          lastSeen: lastSeenAt,
          reason: `Reader last seen ${Math.round(staleDuration / 1000)}s ago`,
          reader,
        };
      }
    }

    // Reader is online and recently seen
    return {
      status: "healthy",
      readerId,
      lastSeen: lastSeenAt || new Date(),
      reader,
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    return {
      status: "offline",
      readerId,
      reason: `Health check failed: ${errorMessage}`,
    };
  }
}

/**
 * POS Health Monitor Actor Implementation
 *
 * Uses fromCallback pattern for long-running background monitoring.
 * Sends events back to parent machine when status changes.
 */
export const posHealthMonitorActorImpl = fromCallback<
  POSHealthEvent,
  POSHealthMonitorInput
>(({ sendBack, input }) => {
  const {
    readerId,
    pollIntervalMs = 30000,
    degradedThresholdMs = 60000,
  } = input;

  // Track previous status to only emit on changes
  let previousStatus: POSHealthStatus = "unknown";
  let intervalId: ReturnType<typeof setInterval> | null = null;

  // If no reader configured, emit NO_READER and don't start polling
  if (!readerId || readerId.trim().length === 0) {
    console.log("[POSHealthMonitor] No reader ID configured, skipping monitor");
    sendBack({ type: "POS_HEALTH.NO_READER" });
    return () => {}; // No cleanup needed
  }

  console.log(
    `[POSHealthMonitor] Starting health monitor for reader ${readerId}`,
  );
  console.log(
    `[POSHealthMonitor] Poll interval: ${pollIntervalMs}ms, Degraded threshold: ${degradedThresholdMs}ms`,
  );

  const performHealthCheck = async () => {
    const result = await checkReaderHealth(readerId, degradedThresholdMs);

    // Only emit events on status change (or first check)
    if (result.status !== previousStatus) {
      console.log(
        `[POSHealthMonitor] Status changed: ${previousStatus} → ${result.status}`,
      );

      // Log to Datadog
      datadogLogs.logger.info("[POSHealthMonitor] Reader status changed", {
        readerId,
        previousStatus,
        newStatus: result.status,
        reason: result.reason,
        lastSeen: result.lastSeen?.toISOString(),
      });

      previousStatus = result.status;

      // Emit appropriate event
      switch (result.status) {
        case "healthy":
          sendBack({
            type: "POS_HEALTH.HEALTHY",
            readerId,
            lastSeen: result.lastSeen || new Date(),
          });
          break;
        case "degraded":
          sendBack({
            type: "POS_HEALTH.DEGRADED",
            readerId,
            reason: result.reason || "Unknown degradation",
          });
          break;
        case "offline":
          sendBack({
            type: "POS_HEALTH.OFFLINE",
            readerId,
            reason: result.reason || "Reader offline",
          });
          break;
      }
    }
  };

  // Perform initial check immediately
  performHealthCheck();

  // Start polling interval
  intervalId = setInterval(performHealthCheck, pollIntervalMs);

  // Cleanup function
  return () => {
    console.log(`[POSHealthMonitor] Stopping health monitor for ${readerId}`);
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };
});

/**
 * Actor metadata for registry
 */
export const posHealthMonitorActor = {
  impl: posHealthMonitorActorImpl,
  metadata: {
    displayName: "posHealthMonitor",
    description:
      "Background actor that monitors POS terminal reader health status",
    category: "data" as const,
    retryable: false,
    timeout: 0, // Long-running, no timeout
    errorCodes: ["NO_READER", "HEALTH_CHECK_FAILED"],
    inputSchema: POSHealthMonitorInputSchema,
  },
};
